// Consolidated BESS widget for the Overview tab — mirrors the «Активна потужність»
// card: SOC ring with power·mode INSIDE the ring, 3 stat tiles below (horizontal)
// and 3 stat tiles on the right (vertical).
import CircularGauge from '@/components/base/CircularGauge';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import type { BessTelemetry } from '@/scada/mock/telemetry';

const n = (v?: number | null, d = 1) => (v == null ? '—' : Number(v).toFixed(d));

// Same dimensions as the «Активна потужність» embedded stat tiles.
function Tile({ l, v, u, c }: { l: string; v: string; u?: string; c?: string }) {
  return (
    <div style={{ background: scada.panel2, border: `1px solid ${scada.border}`, borderRadius: 8, padding: '8px 9px' }}>
      <div style={{ color: scada.textMuted, fontSize: 10, lineHeight: 1.2 }}>{l}</div>
      <div className="flex items-baseline gap-1" style={{ marginTop: 2 }}>
        <span style={{ color: c ?? scada.text, fontSize: 16, fontWeight: 700 }}>{v}</span>
        {u && <span style={{ color: scada.textDim, fontSize: 9 }}>{u}</span>}
      </div>
    </div>
  );
}

export default function BessConsolidated({ bess, capacityKwh, fill }: { bess: BessTelemetry; capacityKwh?: number; fill?: boolean }) {
  const modeLabel = bess.mode === 'charging' ? 'Заряд' : bess.mode === 'discharging' ? 'Розряд' : bess.mode === 'fault' ? 'Аварія' : 'Очікує';
  const modeColor = bess.mode === 'charging' ? scada.state.curtailed : bess.mode === 'discharging' ? scada.state.ok : bess.mode === 'fault' ? scada.state.alarm : scada.textDim;
  const soc = bess.socPct ?? 0;
  // Blue (cyan) SOC ring — same accent as the «Активна потужність» gauge on
  // Bolgrad. Amber only when the charge is critically low; fault is conveyed by
  // the red status text inside the ring, not by the ring colour.
  const socColor = soc >= 20 ? scada.state.curtailed : scada.state.warning;

  return (
    <SectionCard
      fill={fill}
      title="УЗЕ / Накопичувач"
      subtitle={`Консолідований стан BESS${capacityKwh ? ` · ${capacityKwh / 1000} МВт·год` : ''}`}
      right={<span style={{ color: modeColor, fontSize: 12, fontWeight: 600 }}>{modeLabel}</span>}
    >
      <div className="flex flex-col justify-center" style={{ height: '100%', gap: 16 }}>
        {/* Top: ring shifted left (centre over the btn1/btn2 divider) + 3 vertical tiles over btn3 */}
        <div className="flex" style={{ gap: 8 }}>
          <div className="flex justify-center items-center" style={{ flex: 2, minWidth: 0 }}>
            <CircularGauge value={soc} max={100} size={196} strokeWidth={10} color={socColor} trackColor={scada.panel2}>
              <span style={{ color: scada.text, fontSize: 24, fontWeight: 700, lineHeight: 1.1 }}>{n(soc, 1)}</span>
              <span style={{ color: scada.textDim, fontSize: 12 }}>% SOC</span>
              <span style={{ color: modeColor, fontSize: 10, fontWeight: 600, marginTop: 2, textAlign: 'center' }}>
                {Math.abs(bess.powerKw).toFixed(1)} кВт · {modeLabel.toLowerCase()}
              </span>
            </CircularGauge>
          </div>
          <div className="flex flex-col justify-center" style={{ flex: 1, minWidth: 0, gap: 8 }}>
            <Tile l="Доступно зарядити" v={n(bess.chargeAvailableKwh, 0)} u="кВт·год" c={scada.state.curtailed} />
            <Tile l="Доступно розрядити" v={n(bess.dischargeAvailableKwh, 0)} u="кВт·год" c={scada.state.ok} />
            <Tile l="Заряд / розряд" v={`${n(bess.dailyChargeKwh, 0)}/${n(bess.dailyDischargeKwh, 0)}`} u="кВт·год" />
          </div>
        </div>

        {/* Bottom: 3 tiles full width (mirrors «Активна потужність») */}
        <div className="grid grid-cols-3 gap-2">
          <Tile l="SOH" v={n(bess.sohPct, 0)} u="%" />
          <Tile l="Напруга" v={n(bess.batteryV, 0)} u="В" />
          <Tile l="Температура" v={n(bess.innerTempC, 0)} u="°C" />
        </div>
      </div>
    </SectionCard>
  );
}
